import { useCallback, useRef, useState } from 'react'
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import { invalidError, notice } from '@/components/pbo/notice'
import GlobalOrderCollectPageView, { type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { collectGlobalOrders } from './queries/useCollectGlobalOrders'
import { apiErrMessage, toCollectSummary } from './queries/mapping'
import { readCollectForm } from './components/collectFormSeam'
import { LIMIT_MAX, LIMIT_MIN, ORDER_NOS_MAX } from './consts'

// 주문수집 (gl/gl02) — 컨덕터 컴포넌트 모드 배선(문서 51). UI 는 handoff/page.generated.tsx 가 그리고,
// 이 파일은 옛 손코드의 수집 API·검증을 Wiring 슬롯에 잇는다. 생성본은 재핸드오프 때 통째로 덮인다.
//
// 2026-09-22 PD 결정 — **수집 전용 화면**으로 전환. 옛 "주문 수집 2.0"(gl14)에 있던 조회 필터·결과 표와
// 수집 칸 7개(주문번호·배송국가·스타일넘버·상품번호·상품명·해외배송번호·국내송장번호)는 데모에 없어 이 화면에서 뺐다
// (개발 미배포 상태라 손실 아님). 조회가 다시 들어오면 useGetOrderCollectionList·searchQuery 를 git 이력에서 되살린다.

function GlobalOrderCollect() {
  // 생성본 폼에는 값 버스가 없어(codegen 미지원 — collectFormSeam.ts 머리 참고) 클릭 시점에 이 요소 아래 DOM 을 읽는다.
  const rootRef = useRef<HTMLDivElement>(null)
  const [collecting, setCollecting] = useState(false)
  // 생성본 [수집] 버튼엔 loading 이 없다 — 응답이 10분까지 걸릴 수 있는 동기 API 라(useCollectGlobalOrders 머리) 중복 클릭을 여기서 막는다.
  const inFlight = useRef(false)

  const collect = useCallback(async (): Promise<string | false> => {
    if (inFlight.current) return false
    const root = rootRef.current
    if (!root) return false
    const read = readCollectForm(root)

    // 생성본은 슬롯의 reject 를 조용히 삼킨다(토스트 없음) — 검증 실패(토스트)·API 실패(알럿) 모두 여기서 먼저 알린 뒤 throw 한다.
    if (!read.values.collectPeriod) {
      throw invalidError('수집기간은 필수입니다.')
    }
    if (read.orderNoCount === 0) {
      throw invalidError('주문번호는 필수입니다.')
    }
    if (read.orderNoCount > ORDER_NOS_MAX) {
      throw invalidError(`주문번호는 최대 ${ORDER_NOS_MAX}건까지 입력할 수 있습니다(입력 ${read.orderNoCount}건).`)
    }
    // 데모가 '마지막 배송 번호'를 다건 입력으로 바꿨는데 API 는 단건이다 — 2개 이상이면 막고 안내한다(가이드 52 §5-A ⑨). 첫 값만 조용히 보내지 않는다.
    if (read.lastDeliveryCount > 1) {
      throw invalidError(`마지막 배송 번호는 1건만 입력할 수 있습니다(입력 ${read.lastDeliveryCount}건).`)
    }
    if (read.lastDeliveryInvalid) {
      throw invalidError('마지막 배송 번호는 숫자로 입력해 주세요.')
    }
    const { limit } = read.values
    if (limit !== undefined && (limit < LIMIT_MIN || limit > LIMIT_MAX)) {
      throw invalidError(`제한은 ${LIMIT_MIN}~${LIMIT_MAX} 사이여야 합니다.`)
    }

    inFlight.current = true
    setCollecting(true)
    try {
      const result = await collectGlobalOrders(read.values)
      return toCollectSummary(result) // 문자열을 돌려주면 생성본이 그 문구로 성공 토스트를 낸다
    } catch (e) {
      notice.failure(apiErrMessage(e))
      throw e
    } finally {
      inFlight.current = false
      setCollecting(false)
    }
  }, [])

  // ⚠️ useMemo 로 감싸지 않는다(가이드 52 항목 10) — 뷰가 렌더 첫머리에 모듈 홀더로 받으므로 매 렌더 새 객체가 맞다.
  const wiring: Wiring = {
    permission: usePermissionSlot('gl14'),
    rows: {},
    actions: { gl02CollectButton: collect },
    useBusy: () => collecting,
  }

  return (
    <div ref={rootRef}>
      <GlobalOrderCollectPageView wiring={wiring} />
    </div>
  )
}

export default GlobalOrderCollect
